import { CloseOutlined, KeyOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { TeamIcon, UserIcon } from 'assets/images/svg';
import { entityType } from 'config/entityType';
import { groupPermissionsBySourceForDevice } from 'library/helpers/accessControl';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectPermissionsByDeviceID } from 'reduxStore/permissions/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { Loader } from 'zComponents/atoms';
import { Button, Col, Divider, Empty, Flex, Form, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const GrantPermissionsToDeviceModal = ({ visible: visibleProp, setVisible: setVisibleProp, children, deviceID }) => {
	const {
		token: { colorTextLabel, paddingXXS, fontWeightStrong, fontSizeSM, paddingXL, paddingXS, paddingSM, paddingLG }
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const teams = useSelector(selectTeams);
	const people = useSelector(selectPeople);
	const deviceGroups = useSelector(selectAssetGroups);
	const permissions = useSelector((state) => selectPermissionsByDeviceID(state, deviceID));
	const { loadingAddPermission, loadingDeletePermission } = useSelector((state) => state.Permissions);

	const [internalVisible, setInternalVisible] = useState(false);
	const [selectedPeople, setSelectedPeople] = useState([]);
	const [selectedTeams, setSelectedTeams] = useState([]);
	const [removedPermissions, setRemovedPermissions] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);

	const canManage = useManagePermittedRoles('aCPermissionManagement');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
		setSelectedPeople([]);
		setSelectedTeams([]);
		setRemovedPermissions([]);
		setHasChanges(false);
	};

	const handleSubmit = () => {
		// Add new permissions
		if (selectedPeople.length > 0) {
			dispatch(
				permissionsActions.grantPermission({
					deviceIDs: [deviceID],
					personIDs: selectedPeople,
					teamIDs: [], // entityCreation = false
					permissionTypeID: 1
				})
			);
		}

		if (selectedTeams.length > 0) {
			dispatch(
				permissionsActions.grantPermission({
					deviceIDs: [deviceID],
					teamIDs: selectedTeams,
					personIDs: [], // entityCreation = false
					permissionTypeID: 1
				})
			);
		}

		// Remove permissions
		if (removedPermissions.length > 0) {
			removedPermissions.forEach((perm) => {
				const isPerson = perm.entityTypeID === entityType.PERSON;
				dispatch(
					permissionsActions.revokePermission({
						permissionTypeID: 1,
						deviceIDs: [deviceID],
						// If it was via Team, we send the TeamID. If not, NULL.
						teamID: perm.teamID || null,
						// If it was via Group, we send the GroupID. If not, NULL.
						assetGroupID: perm.assetGroupID || null,
						// Identify the target entity
						personIDs: isPerson ? [perm.entityID] : [],
						teamIDs: !isPerson ? [perm.entityID] : []
					})
				);
			});
		}

		handleCancel();
	};

	const displayPermissions = useMemo(() => {
		return groupPermissionsBySourceForDevice({
			permissions,
			people,
			teams,
			deviceGroups
		});
	}, [permissions, people, teams, deviceGroups]);

	const filteredPermissions = displayPermissions.filter(
		(perm) => !removedPermissions.some((removed) => removed.permissionID === perm.permissionID)
	);

	const grantedPersonIDs = new Set(
		filteredPermissions.filter((p) => p.entityTypeID === entityType.PERSON).map((p) => p.entityID)
	);

	const grantedTeamIDs = new Set(
		filteredPermissions.filter((p) => p.entityTypeID === entityType.TEAM).map((p) => p.entityID)
	);

	const peopleOptions = people
		.filter((p) => !grantedPersonIDs.has(p.personID) && !selectedPeople.includes(p.personID))
		.map((p) => ({ label: p.displayName || p.name, value: p.personID }));

	const teamsOptions = teams
		.filter((t) => !grantedTeamIDs.has(t.teamID) && !selectedTeams.includes(t.teamID))
		.map((t) => ({ label: t.name, value: t.teamID }));

	const handleRemovePermission = (perm) => {
		setRemovedPermissions([...removedPermissions, perm]);
		setHasChanges(true);
	};

	const handleAddPerson = (personID) => {
		setSelectedPeople([...selectedPeople, personID]);
		setHasChanges(true);
	};

	const handleAddTeam = (teamID) => {
		setSelectedTeams([...selectedTeams, teamID]);
		setHasChanges(true);
	};

	const handleRemoveSelectedPerson = (personID) => {
		const updated = selectedPeople.filter((id) => id !== personID);
		setSelectedPeople(updated);
		setHasChanges(updated.length > 0 || selectedTeams.length > 0 || removedPermissions.length > 0);
	};

	const handleRemoveSelectedTeam = (teamID) => {
		const updated = selectedTeams.filter((id) => id !== teamID);
		setSelectedTeams(updated);
		setHasChanges(selectedPeople.length > 0 || updated.length > 0 || removedPermissions.length > 0);
	};

	const renderSelectWithTags = (label, placeholder, options, onSelect) => {
		return (
			<Flex vertical gap={paddingXS}>
				<Text style={{ fontWeight: fontWeightStrong }}>{label}</Text>
				<Select
					style={{ width: '100%', marginBottom: paddingSM }}
					showSearch
					placeholder={placeholder}
					options={options}
					value={null}
					onSelect={onSelect}
					filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
				/>
			</Flex>
		);
	};

	return (
		<>
			<Tooltip title={!canManage ? permissionDeniedMessage : 'Manage Permissions'} placement='top'>
				<span onClick={(e) => canManage && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<KeyOutlined color='currentColor' />}
							disabled={!canManage}
						>
							Manage Permissions
						</Button>
					)}
				</span>
			</Tooltip>
			<Modal
				open={visible}
				closable
				onCancel={handleCancel}
				width={763}
				centered
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
				footer={[
					<Divider key='divider' />,
					<Flex justify='flex-end' gap={12} key='footer'>
						<Button onClick={handleCancel}>Cancel</Button>
						<Button
							type='primary'
							onClick={handleSubmit}
							loading={loadingAddPermission || loadingDeletePermission}
							disabled={!hasChanges}
						>
							Save Changes
						</Button>
					</Flex>
				]}
			>
				<Flex vertical style={{ marginBottom: paddingLG }}>
					<Title level={4} style={{ margin: 0, lineHeight: '28px' }}>
						Manage Permissions
					</Title>
					<Text style={{ color: colorTextLabel, lineHeight: '20px', fontSize: fontSizeSM }}>
						Manage who has access to this device by adding or removing people and teams.
					</Text>
				</Flex>
				<Form form={form} layout='vertical'>
					<Row>
						<Col span={24}>
							<Flex gap={paddingLG} style={{ marginBottom: paddingLG }} vertical>
								{renderSelectWithTags('Add People', 'Select People', peopleOptions, handleAddPerson)}
								{renderSelectWithTags('Add Teams', 'Select Teams', teamsOptions, handleAddTeam)}
							</Flex>
							<Text style={{ fontWeight: fontWeightStrong }}>Current Access Permissions</Text>

							<Flex wrap='wrap' style={{ marginTop: paddingSM }}>
								{loadingDeletePermission ? (
									<Flex justify='center' align='center' style={{ width: '100%' }}>
										<Loader />
									</Flex>
								) : filteredPermissions.length === 0 &&
								  selectedPeople.length === 0 &&
								  selectedTeams.length === 0 &&
								  removedPermissions.length === 0 ? (
									<Flex
										align='center'
										justify='center'
										style={{
											width: '100%',
											height: 85
										}}
									>
										<Empty
											image={Empty.PRESENTED_IMAGE_SIMPLE}
											description='No permissions assigned yet. Select people or teams above to grant access.'
										/>
									</Flex>
								) : (
									<Flex
										wrap='wrap'
										gap={paddingSM}
										align='flex-start'
										style={{
											height: 85,
											overflowY: 'auto',
											alignContent: 'flex-start',
											alignItems: 'flex-start'
										}}
									>
										{/* Existing permissions (not marked for removal) */}
										{filteredPermissions.map((perm) => (
											<Tooltip key={`existing-${perm.permissionID}`} title={perm.tooltip}>
												<Tag
													color={perm.badgeColor}
													style={{
														fontSize: fontSizeSM,
														fontWeight: fontWeightStrong,
														display: 'flex',
														alignItems: 'center',
														borderColor: 'currentColor',
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														{perm.Icon && <perm.Icon color='currentColor' />}
														{perm.label}
														<CloseOutlined
															style={{
																cursor: 'pointer',
																color: 'inherit',
																marginLeft: 4
															}}
															onClick={() => handleRemovePermission(perm)}
														/>
													</Flex>
												</Tag>
											</Tooltip>
										))}
										{/* Newly selected people */}

										{selectedPeople.map((personID) => (
											<Tag
												key={`new-p-${personID}`}
												closable
												color='purple'
												style={{
													fontSize: fontSizeSM,
													fontWeight: fontWeightStrong,
													display: 'flex',
													alignItems: 'center',
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
												onClose={() => handleRemoveSelectedPerson(personID)}
											>
												<UserIcon color='currentColor' style={{ marginRight: 4 }} />{' '}
												{people.find((p) => p.personID === personID)?.displayName} (New)
											</Tag>
										))}
										{/* Newly selected teams */}

										{selectedTeams.map((teamID) => (
											<Tag
												key={`new-team-${teamID}`}
												closable
												color='cyan'
												style={{
													fontSize: fontSizeSM,
													fontWeight: fontWeightStrong,
													display: 'flex',
													alignItems: 'center',
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
												onClose={() => handleRemoveSelectedTeam(teamID)}
											>
												<TeamIcon color='currentColor' style={{ marginRight: 4 }} />{' '}
												{teams.find((t) => t.teamID === teamID)?.name} (New)
											</Tag>
										))}
									</Flex>
								)}
							</Flex>
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	);
};

export default GrantPermissionsToDeviceModal;
